import {
  absMoney,
  addMoney,
  compareMoney,
  daysBetween,
  equalsMoney,
  type LocalDate,
  type Money,
  niceMoneyTicks,
  splitMoneyEvenly,
  subtractMoney,
} from '@taler/domain'

export interface TrendPoint {
  readonly date: LocalDate
  readonly netWorth: Money
}

/** Round axis steps covering the series; zero is a tick only when the range spans it. */
export function trendTicks(points: readonly TrendPoint[], targetCount: number): readonly Money[] {
  const first = points[0]
  if (first === undefined) return []
  let min = first.netWorth
  let max = min
  for (const point of points) {
    if (compareMoney(point.netWorth, min) < 0) min = point.netWorth
    if (compareMoney(point.netWorth, max) > 0) max = point.netWorth
  }
  if (equalsMoney(min, max)) {
    const [half] = splitMoneyEvenly(absMoney(min), 2)
    if (half === undefined) return []
    return niceMoneyTicks(subtractMoney(min, half), addMoney(max, half), targetCount)
  }
  return niceMoneyTicks(min, max, targetCount)
}

/** Position of each point across the series' span, 0 at the first point and 1 at the last. */
export function trendFractions(points: readonly TrendPoint[]): readonly number[] {
  const first = points[0]
  const last = points.at(-1)
  if (first === undefined || last === undefined) return []
  const span = daysBetween(first.date, last.date)
  return points.map((point) => (span === 0 ? 0 : daysBetween(first.date, point.date) / span))
}

/** Index of the point whose fraction is closest to `fraction`. */
export function nearestTrendIndex(fractions: readonly number[], fraction: number): number {
  let best = 0
  for (let index = 1; index < fractions.length; index += 1) {
    if (Math.abs(fractions[index]! - fraction) < Math.abs(fractions[best]! - fraction)) best = index
  }
  return best
}
